import { useCallback, useId, useMemo, useState, startTransition, type FC } from "react";
import { ComboboxProvider } from "@ariakit/react";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import {
    Select,
    SelectContentCombobox,
    SelectItemCombobox,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { cn } from "@/components/podkit/lib/cn";
import type { PlainMessage } from "@bufbuild/protobuf";

type ExecutorOption = {
    id: string;
    name: string;
    principal: Principal.SERVICE_ACCOUNT | Principal.USER;
    avatarUrl?: string;
};

type WorkflowEditExecutorProps = {
    selectedExecutorId?: string;
    onExecutorChange: (executor?: PlainMessage<Subject>) => void;
    disabled?: boolean;
    className?: string;
};

export const WorkflowEditExecutor: FC<WorkflowEditExecutorProps> = ({
    selectedExecutorId,
    onExecutorChange,
    disabled = false,
    className,
}) => {
    const selectId = useId();
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const { data: serviceAccountsData, isPending: isLoadingServiceAccounts } = useListServiceAccounts();
    const { data: currentUser, isPending: isLoadingUser } = useAuthenticatedUser();

    const executorOptions = useMemo((): ExecutorOption[] => {
        const options: ExecutorOption[] = [];

        if (currentUser) {
            options.push({
                id: currentUser.id,
                name: currentUser.name,
                principal: Principal.USER,
                avatarUrl: currentUser.avatarUrl,
            });
        }

        if (serviceAccountsData?.serviceAccounts) {
            serviceAccountsData.serviceAccounts.forEach((sa) => {
                options.push({
                    id: sa.id,
                    name: sa.name,
                    principal: Principal.SERVICE_ACCOUNT,
                });
            });
        }

        return options;
    }, [currentUser, serviceAccountsData]);

    const filteredOptions = useMemo(() => {
        if (!searchValue) return executorOptions;

        const words = searchValue.toLowerCase().split(" ");
        return executorOptions.filter((option) => {
            const label = option.name.toLowerCase();
            return words.every((word) => label.includes(word));
        });
    }, [executorOptions, searchValue]);

    const selectedExecutor = useMemo(() => {
        return executorOptions.find((option) => option.id === selectedExecutorId);
    }, [executorOptions, selectedExecutorId]);

    const handleExecutorChange = useCallback(
        (executorId: string) => {
            const executor = executorOptions.find((option) => option.id === executorId);
            if (executor) {
                onExecutorChange({
                    id: executor.id,
                    principal: executor.principal,
                });
            }
            setIsSelectOpen(false);
        },
        [executorOptions, onExecutorChange],
    );

    const isLoading = isLoadingServiceAccounts || isLoadingUser;

    return (
        <div className={cn(className)}>
            <Select
                open={isSelectOpen}
                onOpenChange={setIsSelectOpen}
                onValueChange={handleExecutorChange}
                value={selectedExecutorId || ""}
                disabled={disabled}
            >
                <ComboboxProvider
                    open={isSelectOpen}
                    setOpen={setIsSelectOpen}
                    resetValueOnHide
                    includesBaseElement={false}
                    setValue={(value) => {
                        startTransition(() => {
                            setSearchValue(value.toLowerCase());
                        });
                    }}
                >
                    <SelectTrigger id={selectId} loading={isLoading}>
                        <SelectValue placeholder="Select identity">
                            {selectedExecutor ? <ExecutorOption option={selectedExecutor} /> : "Select identity"}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContentCombobox inputPlaceholder="Search..." className="max-w-[calc(100vw-1rem)]">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <SelectItemCombobox key={option.id} value={option.id} className="cursor-pointer">
                                    <ExecutorOption option={option} />
                                </SelectItemCombobox>
                            ))
                        ) : (
                            <div className="px-2 py-1 text-sm text-content-muted">
                                {isLoading
                                    ? "Loading identities..."
                                    : searchValue
                                      ? "No identities found"
                                      : "No identities available"}
                            </div>
                        )}
                    </SelectContentCombobox>
                </ComboboxProvider>
            </Select>
        </div>
    );
};

const ExecutorOption: FC<{ option: ExecutorOption }> = ({ option }) => {
    const { id, name, principal, avatarUrl } = option;
    return (
        <div className="flex items-center gap-2">
            {principal === Principal.USER ? (
                <Avatar size={24}>
                    {avatarUrl && <AvatarImage src={avatarUrl} alt={`${name}'s avatar`} />}
                    <AvatarFallback>
                        <AvatarInitials name={name} size={24} />
                    </AvatarFallback>
                </Avatar>
            ) : (
                <ServiceAccountAvatar id={id} size={24} />
            )}
            <span className="text-base">{name}</span>
        </div>
    );
};
